import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { Field, TemplateDetails, TemplateTag } from '@/api/types';
import { EditorViewMode, FieldsPanelView, type Layer } from './types';

interface State {
    // Currently selected variant when editor is in LIST mode
    currentVariantIndex: number;
    // Currently displayed view mode in the editor
    editorViewMode: EditorViewMode;
    // Currently displayed view in the fields panel
    fieldsPanelView: FieldsPanelView;
    // Width of the fields panel. Used for displaying consistent width across different views
    fieldsPanelWidth: number;
    // Currently highlighted layer in the SVG
    highlightedLayer: Layer | null;
    // Currently selected field's in-progress (unsaved) "dirty" data used to coordinate changes
    // between the fields panel and the editor
    editedFieldData: Field | null;
    // Currently selected field in the fields panel
    editedFieldId: number | null;
    // Currently selected layer in the SVG
    selectedLayer: Layer | null;
    // Currently edited template
    template: TemplateDetails | null;
    // Svg content for all the variants;
    svgFiles: Record<string, string>;
}

type Actions = {
    setCurrentVariantIndex: (index: number) => void;
    setEditedFieldData: (field: Field | null) => void;
    setEditedFieldId: (id: number | null) => void;
    setEditorViewMode: (mode: EditorViewMode) => void;
    setFields: (fields: Field[]) => void;
    setFieldsPanelView: (view: FieldsPanelView) => void;
    setHighlightedLayer: (layer: Layer | null) => void;
    setIsEdited(): void;
    setSelectedLayer: (layer: Layer | null) => void;
    setTemplate: (template: TemplateDetails) => void;
    setTemplateTags: (tags: TemplateTag[]) => void;
    addSvgFile: (url: string, svgFile: string) => void;
};

export const useTemplateEditorStore = create<State & Actions>()(
    subscribeWithSelector(
        immer((set) => ({
            currentVariantIndex: 0,
            editorViewMode: EditorViewMode.List,
            fieldsPanelView: FieldsPanelView.Default,
            fieldsPanelWidth: 360,
            highlightedLayer: null,
            editedFieldData: null,
            editedFieldId: null,
            selectedLayer: null,
            template: null,
            svgFiles: {},
            setCurrentVariantIndex: (index) => set({ currentVariantIndex: index }),
            setEditorViewMode: (mode) => set({ editorViewMode: mode }),
            setFields: (fields) => {
                set((state) => {
                    if (state.template) {
                        state.template.latestVersion.fields = fields;
                    }
                    return state;
                });
            },
            setFieldsPanelView: (view) => set({ fieldsPanelView: view }),
            setHighlightedLayer: (layer) => set({ highlightedLayer: layer }),
            setEditedFieldData: (field) => set({ editedFieldData: field }),
            setEditedFieldId: (id) => set({ editedFieldId: id }),
            setSelectedLayer: (layer) => set({ selectedLayer: layer }),
            setTemplateTags: (tags) => {
                set((state) => {
                    if (state.template) {
                        state.template.tags = tags;
                    }
                    return state;
                });
            },
            addSvgFile: (url, svgFile) => {
                set((state) => {
                    state.svgFiles = { ...state.svgFiles, [url]: svgFile };
                    return state;
                });
            },
            setTemplate: (template: TemplateDetails) => set({ template }),
            setIsEdited: () =>
                set((state) => {
                    if (state.template) {
                        state.template.latestVersion.isEdited = true;
                    }
                }),
        })),
    ),
);
